/* eslint-disable no-nested-ternary */
import {
  BlueBirdTheme, Checkbox, Dropdown, Form, Typography,
} from "@kitbird/core";
import {
  Control, Controller, UseFormSetValue,
} from "react-hook-form";
import {
  CardBody, CardContainer, CardHeader, CardTitle,
} from "../BusinessCard";
import { InlineFlexWrapper, LayoutContainer } from "../../BusinessTrx.styled";
import { getOptionLabel } from "../../utils";
import { IBusinessTrxFormValues } from "../../types";
import { useFormBusinessTrx } from "../../FormBusinessTrxProvider";

interface StepTransactionDetailsProps {
  control: Control<IBusinessTrxFormValues>;
  setValue: UseFormSetValue<IBusinessTrxFormValues>;
}

const StepTransactionDetails = ({ control, setValue }: StepTransactionDetailsProps) => {
  const {
    trxGlOptions,
    fetchNextGl,
    hasNextGl,
    setDebouncedSearchGl,
    trxTypeOptions,
    isEditMode,
    detailTransactionValues,
    rawTrxGl,
  } = useFormBusinessTrx();

  return (
    <CardContainer style={{ overflowY: "visible" }}>
      <CardHeader>
        <CardTitle>
          <Typography level={4} fontWeight="bold" color={BlueBirdTheme.colors.gray.subtitle}>
            Detail transaksi
          </Typography>
        </CardTitle>
      </CardHeader>
      <CardBody gridTemplateColumns="1fr 1fr">
        <LayoutContainer layout="flex" direction="column">
          <Controller
            name="detailTransaksi.gl"
            control={control}
            render={({ field, fieldState: { error } }) => {
              const compositeValue = detailTransactionValues.gl && detailTransactionValues.deskripsi
                ? `${detailTransactionValues.gl}•${detailTransactionValues.deskripsi}`
                : "";
              const displayLabelFromOptions = getOptionLabel(trxGlOptions, compositeValue);
              const initialDisplayLabel = isEditMode
                && detailTransactionValues.gl
                && detailTransactionValues.deskripsi
                ? `${detailTransactionValues.gl} • ${detailTransactionValues.deskripsi}`
                : compositeValue;

              return (
                <Dropdown
                  {...field}
                  id={field.name}
                  value={displayLabelFromOptions || initialDisplayLabel}
                  onSelected={(selectedVal) => {
                    const compositeKey = selectedVal.value;
                    const foundGl = rawTrxGl?.pages.flatMap((p) => p.lstsmplmsttrxcode)
                      .find((v) => `${v.gnlldr}•${v.trx_desc}` === compositeKey);
                    if (foundGl) {
                      setValue("detailTransaksi.gl", foundGl.gnlldr, { shouldValidate: true, shouldDirty: true });
                      setValue("detailTransaksi.deskripsi", foundGl.trx_desc, { shouldValidate: true, shouldDirty: true });
                      setValue("detailTransaksi.deskripsiSap", foundGl.sap_bktxt, { shouldValidate: true, shouldDirty: true });
                      setValue("detailTransaksi.sap_shkzg", foundGl.sap_shkzg, { shouldValidate: true, shouldDirty: true });
                      setValue("detailTransaksi.trx_code", foundGl.trx_code, { shouldValidate: true, shouldDirty: true });
                    }
                  }}
                  options={trxGlOptions || []}
                  error={error?.message ? { message: error.message } : undefined}
                  label="GL & Deskripsi"
                  allowSearch
                  required
                  fetchNextPage={fetchNextGl}
                  hasNextPage={hasNextGl}
                  onSearch={setDebouncedSearchGl}
                  disabled={isEditMode}
                />
              );
            }}
          />
          <Controller
            name="detailTransaksi.deskripsiSap"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Form
                {...field}
                type="text"
                label="Deskripsi SAP"
                placeholder="Deskripsi SAP"
                required
                error={error?.message ? { message: error.message } : undefined}
                disabled
              />
            )}
          />
          <Controller
            name="detailTransaksi.tipeTransaksi"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Dropdown
                {...field}
                id={field.name}
                value={getOptionLabel(trxTypeOptions, field.value, true) || field.value}
                onSelected={(selectedVal) => field.onChange(selectedVal.value)}
                options={trxTypeOptions || []}
                error={error?.message ? { message: error.message } : undefined}
                label="Tipe transaksi"
                allowSearch
                required
                disabled={isEditMode}
              />
            )}
          />
        </LayoutContainer>
        <LayoutContainer layout="flex" align="flex-start" direction="column" gap="8px">
          <Typography level={7} color={BlueBirdTheme.colors.gray.disable} fontWeight="regular">
            Debit/kredit <span style={{ color: "#BE321E" }}>*</span>
          </Typography>
          <Controller
            name="detailTransaksi.sap_shkzg"
            control={control}
            render={({ field, fieldState: { error } }) => {
              const isDebitChecked = field.value === "S" || field.value === "X";
              const isKreditChecked = field.value === "H" || field.value === "X";
              const handleCheckboxChange = (changed: "debit" | "kredit", isChecked: boolean) => {
                const newDebitState = changed === "debit" ? isChecked : isDebitChecked;
                const newKreditState = changed === "kredit" ? isChecked : isKreditChecked;
                let newValue = "";
                if (newDebitState && newKreditState) {
                  newValue = "X";
                } else if (newDebitState) {
                  newValue = "S";
                } else if (newKreditState) {
                  newValue = "H";
                }
                field.onChange(newValue);
              };
              return (
                <>
                  <InlineFlexWrapper>
                    <Checkbox
                      label="Debit"
                      checked={isDebitChecked}
                      onChange={(e) => handleCheckboxChange("debit", e.target.checked)}
                      disabled
                    />
                    <Checkbox
                      label="Kredit"
                      checked={isKreditChecked}
                      onChange={(e) => handleCheckboxChange("kredit", e.target.checked)}
                      disabled
                    />
                  </InlineFlexWrapper>
                  {error && (
                    <Typography level={8} color={BlueBirdTheme.colors.danger[500]}>
                      {error.message}
                    </Typography>
                  )}
                </>
              );
            }}
          />
        </LayoutContainer>
      </CardBody>
    </CardContainer>
  );
};

export default StepTransactionDetails;
